/**
 * BotFitters photo viewer: full-screen photos with a thumbnail strip.
 *
 *   window.bfLightbox.open([{ src, alt }], startIndex, title)
 *
 * Any element with [data-bf-gallery] opens it on click; the photos come from the JSON in
 * the element referenced by data-bf-gallery (an id) or its first
 * script[type="application/json"][data-bf-gallery-images] child.
 */
(function () {
  if (window.bfLightbox) return;

  /** @type {HTMLDialogElement | null} */
  let dialog = null;
  /** @type {{src: string, alt: string}[]} */
  let images = [];
  let index = 0;
  /** @type {Element | null} */
  let opener = null;

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'bf-lightbox';
    dialog.setAttribute('aria-label', 'Product photos');
    dialog.innerHTML = `
      <div class="bf-lightbox__top">
        <p class="bf-lightbox__title"></p>
        <span class="bf-lightbox__count" aria-live="polite"></span>
        <button type="button" class="bf-lightbox__close" aria-label="Close photos">
          <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 4l12 12M16 4 4 16"/></svg>
        </button>
      </div>
      <div class="bf-lightbox__stage">
        <button type="button" class="bf-lightbox__nav bf-lightbox__nav--prev" aria-label="Previous photo">
          <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 4 7 11l7 7"/></svg>
        </button>
        <img class="bf-lightbox__img" alt="">
        <button type="button" class="bf-lightbox__nav bf-lightbox__nav--next" aria-label="Next photo">
          <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m8 4 7 7-7 7"/></svg>
        </button>
      </div>
      <div class="bf-lightbox__thumbs" role="list"></div>`;
    document.body.appendChild(dialog);

    dialog.querySelector('.bf-lightbox__close')?.addEventListener('click', close);
    dialog.querySelector('.bf-lightbox__nav--prev')?.addEventListener('click', () => show(index - 1));
    dialog.querySelector('.bf-lightbox__nav--next')?.addEventListener('click', () => show(index + 1));
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) close();
      const thumb = /** @type {HTMLElement} */ (e.target).closest?.('[data-i]');
      if (thumb) show(Number(/** @type {HTMLElement} */ (thumb).dataset.i));
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') show(index + 1);
      if (e.key === 'ArrowLeft') show(index - 1);
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('bf-lightbox-open');
      if (opener instanceof HTMLElement) opener.focus();
    });

    // Swipe on phones
    let startX = 0;
    const stage = dialog.querySelector('.bf-lightbox__stage');
    stage?.addEventListener('touchstart', (e) => { startX = /** @type {TouchEvent} */ (e).touches[0].clientX; }, { passive: true });
    stage?.addEventListener('touchend', (e) => {
      const dx = /** @type {TouchEvent} */ (e).changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
    });
  }

  /** @param {number} i */
  function show(i) {
    if (!dialog || !images.length) return;
    index = (i + images.length) % images.length;
    const img = /** @type {HTMLImageElement} */ (dialog.querySelector('.bf-lightbox__img'));
    img.src = images[index].src;
    img.alt = images[index].alt || '';
    const count = dialog.querySelector('.bf-lightbox__count');
    if (count) count.textContent = `${index + 1} / ${images.length}`;
    dialog.querySelectorAll('[data-i]').forEach((t) => {
      const on = Number(/** @type {HTMLElement} */ (t).dataset.i) === index;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-current', String(on));
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
    const single = images.length < 2;
    dialog.querySelectorAll('.bf-lightbox__nav').forEach((b) => b.toggleAttribute('hidden', single));
  }

  /**
   * @param {{src: string, alt?: string}[]} list
   * @param {number} [start]
   * @param {string} [title]
   */
  function open(list, start = 0, title = '') {
    if (!list?.length) return;
    if (!dialog) build();
    if (!dialog) return;
    opener = document.activeElement;
    images = list.map((x) => ({ src: x.src, alt: x.alt || '' }));
    const t = dialog.querySelector('.bf-lightbox__title');
    if (t) t.textContent = title;
    const thumbs = dialog.querySelector('.bf-lightbox__thumbs');
    if (thumbs) {
      thumbs.innerHTML = images
        .map((x, i) => `<button type="button" role="listitem" class="bf-lightbox__thumb" data-i="${i}" aria-label="Photo ${i + 1}"><img src="${x.src.replace(/width=\d+/, 'width=160')}" alt="" loading="lazy"></button>`)
        .join('');
    }
    document.documentElement.classList.add('bf-lightbox-open');
    dialog.showModal();
    show(start);
  }

  function close() {
    dialog?.close();
  }

  document.addEventListener('click', (e) => {
    const trigger = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest?.('[data-bf-gallery]'));
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    const ref = trigger.dataset.bfGallery;
    const source = (ref && document.getElementById(ref)) || trigger.querySelector('script[data-bf-gallery-images]');
    try {
      const list = JSON.parse(source?.textContent || '[]');
      open(list, Number(trigger.dataset.bfGalleryStart) || 0, trigger.dataset.bfGalleryTitle || '');
    } catch (_) {
      /* no-op */
    }
  }, true);

  window.bfLightbox = { open, close };
})();
